import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { useBusinessName } from '../contexts/BusinessContext';
import AppointmentFormModal from '../components/AppointmentFormModal';
import UserJobUpdateModal from '../components/UserJobUpdateModal';
import { ApexReference, JobSearch } from '../components/ApexReference';
import { JobDetailLauncher } from '../components/JobDetailModal';
import { matchesJobSearch } from '../lib/jobReference';

export default function AppointmentsPage() {
  const [appointments, setAppointments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [showUserUpdateModal, setShowUserUpdateModal] = useState(false);
  const [updatingAppointment, setUpdatingAppointment] = useState(null);
  const [search, setSearch] = useState('');
  const visibleAppointments = appointments.filter(job => matchesJobSearch(job, search));
  const navigate = useNavigate();
  const { businessName } = useBusinessName();
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchAppointments();
    if (user.is_admin) {
      fetchUsers();
    }
  }, []);

  const fetchAppointments = async () => {
    try {
      const response = await axios.get(`${API}/appointments`);
      setAppointments(response.data);
    } catch (error) {
      toast.error('Failed to fetch appointments');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingAppointment(null);
    fetchAppointments();
  };

  const handleDownloadCalendar = async (appointmentId) => {
    try {
      const response = await axios.get(`${API}/appointments/${appointmentId}/calendar.ics`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `appointment_${appointmentId}.ics`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Calendar file downloaded!');
    } catch (error) {
      toast.error('Failed to download calendar file');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Urgent': return '#FF0000';
      case 'High': return '#FF6B00';
      case 'Medium': return '#FFFF00';
      case 'Low': return '#00FF00';
      default: return '#FFFFFF';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return '#00FFFF';
      case 'Scheduled': return '#00FFFF';
      case 'In Progress': return '#FFFF00';
      case 'Waiting for Customer': return '#FF00FF';
      case 'Waiting for Parts': return '#FF6B00';
      case 'Completed': return '#00FF00';
      case 'Closed': return '#00FF00';
      case 'Cancelled': return '#FF0000';
      default: return '#FFFFFF';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4 md:p-8" style={{ background: '#000000' }}>
        <div className="retro-panel text-center py-12">
          <div className="text-2xl glow-text" style={{ color: '#FF6B00' }}>
            [LOADING APPOINTMENTS...]
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8" style={{ background: '#000000' }}>
      {/* Header */}
      <div className="retro-panel mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold glow-text" style={{ color: '#FF6B00', fontFamily: 'Orbitron, sans-serif' }}>
              {businessName}
            </h1>
            <div className="text-lg mt-1" style={{ color: '#ffffff' }}>
              USER: {user.username} | MY APPOINTMENTS
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/')}
              data-testid="back-to-dashboard-button"
              className="retro-btn retro-btn-green"
            >
              [DASHBOARD]
            </button>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="retro-btn retro-btn-magenta"
            >
              [LOGOUT]
            </button>
          </div>
        </div>
      </div>

      {/* Create Appointment Button (Admin Only) */}
      {user.is_admin && (
        <div className="retro-panel mb-6">
          <button
            onClick={() => {
              setEditingAppointment(null);
              setShowForm(true);
            }}
            data-testid="create-appointment-button"
            className="retro-btn"
          >
            [+ CREATE APPOINTMENT]
          </button>
        </div>
      )}

      {/* Appointments List */}
      <div className="retro-panel">
        <h2 className="text-3xl font-bold mb-6 glow-text" style={{ color: '#FF6B00', fontFamily: 'Orbitron, sans-serif' }}>
          [MY JOB APPOINTMENTS] - TOTAL: {visibleAppointments.length}
        </h2>
        <JobSearch scope="appointments" value={search} onChange={setSearch} />

        {visibleAppointments.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-2xl" style={{ color: '#ffffff' }}>
              [NO APPOINTMENTS ASSIGNED]
            </div>
            <div className="text-lg mt-4" style={{ color: '#FF6B00' }}>
              Contact your administrator for job assignments
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {visibleAppointments.map((appointment) => (
              <div
                key={appointment.id}
                className="border-2 p-4"
                style={{ borderColor: '#FF6B00', background: 'rgba(255, 107, 0, 0.05)' }}
                data-testid={`appointment-${appointment.id}`}
              >
                <div className="flex flex-col md:flex-row justify-between gap-4">
                  <div className="flex-1">
                    <div className="mb-3"><ApexReference job={appointment} scope="appointments" /></div>
                    <div className="text-2xl font-bold mb-2" style={{ color: '#ffffff' }}>
                      {appointment.customer_name}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span style={{ color: '#FF6B00' }}>Date:</span>{' '}
                        <span style={{ color: '#ffffff' }}>{appointment.appointment_date}</span>
                      </div>
                      <div>
                        <span style={{ color: '#FF6B00' }}>Time:</span>{' '}
                        <span style={{ color: '#ffffff' }}>{appointment.start_time} - {appointment.end_time}</span>
                      </div>
                      <div>
                        <span style={{ color: '#FF6B00' }}>Job Type:</span>{' '}
                        <span style={{ color: '#ffffff' }}>{appointment.job_type}</span>
                      </div>
                      <div>
                        <span style={{ color: '#FF6B00' }}>Location:</span>{' '}
                        <span style={{ color: '#ffffff' }}>{appointment.location}</span>
                      </div>
                      <div>
                        <span style={{ color: '#FF6B00' }}>Priority:</span>{' '}
                        <span style={{ color: getPriorityColor(appointment.priority), fontWeight: 700 }}>
                          {appointment.priority}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: '#FF6B00' }}>Status:</span>{' '}
                        <span style={{ color: getStatusColor(appointment.status), fontWeight: 700 }}>
                          {appointment.status}
                        </span>
                      </div>
                    </div>
                    <div className="mt-3">
                      <div style={{ color: '#FF6B00' }}>Description:</div>
                      <div style={{ color: '#ffffff' }}>{appointment.description}</div>
                    </div>
                    {appointment.notes && (
                      <div className="mt-3">
                        <div style={{ color: '#FF6B00' }}>Office Notes:</div>
                        <div style={{ color: '#FFFF00' }}>{appointment.notes}</div>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <JobDetailLauncher appointment={appointment} onUpdate={fetchAppointments} scope="appointments" />
                    <button
                      onClick={() => {
                        setUpdatingAppointment(appointment);
                        setShowUserUpdateModal(true);
                      }}
                      data-testid={`update-job-${appointment.id}`}
                      className="retro-btn retro-btn-green"
                    >
                      [UPDATE JOB]
                    </button>
                    <button
                      onClick={() => handleDownloadCalendar(appointment.id)}
                      data-testid={`download-calendar-${appointment.id}`}
                      className="retro-btn retro-btn-yellow"
                    >
                      [ADD TO CALENDAR]
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Appointment Form Modal */}
      {showForm && (
        <AppointmentFormModal
          appointment={editingAppointment}
          users={users}
          onSuccess={handleFormSuccess}
          onCancel={() => {
            setShowForm(false);
            setEditingAppointment(null);
          }}
        />
      )}

      {/* User Job Update Modal */}
      {showUserUpdateModal && updatingAppointment && (
        <UserJobUpdateModal
          appointment={updatingAppointment}
          onSuccess={() => {
            setShowUserUpdateModal(false);
            setUpdatingAppointment(null);
            fetchAppointments();
          }}
          onClose={() => {
            setShowUserUpdateModal(false);
            setUpdatingAppointment(null);
          }}
        />
      )}
    </div>
  );
}